import { Button } from "@heroui/react";
import { memo, useEffect, useState } from "react";
import { TestimonialCarousel, type Testimonial } from "@funnel-ui/primitives/TestimonialCarousel";

type LoadingStep = {
  label: string;
  durationMs?: number;
};

type LoadingScreenProps = {
  headline?: string;
  steps?: LoadingStep[];
  /** Total target duration; the underlying steps are stretched to fit. */
  totalMs?: number;
  testimonials?: Testimonial[];
  onComplete?: () => void;
  onSkip?: () => void;
  showSkipAfterMs?: number;
  className?: string;
  testId?: string;
};

const DEFAULT_STEPS: LoadingStep[] = [
  { label: "Reading your answers" },
  { label: "Matching personality traits" },
  { label: "Finding ideal characters" },
  { label: "Tuning the conversation style" },
];

const StepList = ({ steps, currentStep }: { steps: LoadingStep[]; currentStep: number }) => (
  <ul className="flex w-full max-w-sm flex-col gap-2">
    {steps.map((step, index) => {
      const isDone = index < currentStep;
      const isActive = index === currentStep;
      return (
        <li
          key={step.label}
          className="flex items-center gap-3 text-sm"
          style={{
            color:
              isDone || isActive ? "hsl(var(--brand-bg-foreground))" : "hsl(var(--hero-fg-muted))",
          }}
        >
          <span
            className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs"
            style={{
              background: isDone
                ? "hsl(var(--hero-success))"
                : isActive
                  ? "hsl(var(--brand-primary))"
                  : "hsl(var(--brand-bg-foreground) / 0.10)",
              color:
                isDone || isActive
                  ? "hsl(var(--brand-primary-foreground))"
                  : "hsl(var(--hero-fg-muted))",
            }}
          >
            {isDone ? "✓" : isActive ? "•" : ""}
          </span>
          <span>{step.label}</span>
        </li>
      );
    })}
  </ul>
);

const LoadingScreenImpl = ({
  headline = "Tuning your match...",
  steps = DEFAULT_STEPS,
  totalMs = 12000,
  testimonials,
  onComplete,
  onSkip,
  showSkipAfterMs = 6000,
  className,
  testId,
}: LoadingScreenProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [skipVisible, setSkipVisible] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!onSkip) return;
    const t = setTimeout(() => setSkipVisible(true), showSkipAfterMs);
    return () => clearTimeout(t);
  }, [onSkip, showSkipAfterMs]);

  useEffect(() => {
    const stepDuration = totalMs / steps.length;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number): void => {
      const elapsed = now - start;
      const ratio = Math.min(1, elapsed / totalMs);
      setProgress(ratio);
      const stepIndex = Math.min(steps.length - 1, Math.floor(elapsed / stepDuration));
      setCurrentStep(stepIndex);
      if (ratio >= 1) {
        onComplete?.();
      } else {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [steps.length, totalMs, onComplete]);

  return (
    <section
      data-testid={testId}
      className={`flex flex-col items-center gap-6 py-6 ${className ?? ""}`}
    >
      <h1
        className="text-center text-xl font-semibold"
        style={{ color: "hsl(var(--brand-bg-foreground))" }}
      >
        {headline}
      </h1>

      <StepList steps={steps} currentStep={currentStep} />

      <div
        className="h-1 w-full max-w-sm overflow-hidden rounded-full"
        style={{ background: "hsl(var(--brand-bg-foreground) / 0.10)" }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${progress * 100}%`,
            background: "hsl(var(--brand-primary))",
            transition: "width 60ms linear",
          }}
        />
      </div>

      {testimonials && testimonials.length > 0 && (
        <div className="w-full max-w-sm pt-4">
          <TestimonialCarousel items={testimonials} />
        </div>
      )}

      {skipVisible && onSkip && (
        <Button
          variant="ghost"
          size="sm"
          onPress={onSkip}
          className="underline underline-offset-2 opacity-70"
        >
          Skip the wait
        </Button>
      )}
    </section>
  );
};

export const LoadingScreen = memo(LoadingScreenImpl);

export default LoadingScreen;
